"use client";

import BFLApiFailure from "@repo/shared-ui/BFLApiFailure";
import { useSearchParams } from 'next/navigation';
import React, { Suspense, useEffect, useState } from 'react';
import type { IStockItem } from '../../interfaces/StockList.interface';
import type { StockListingTableAEMContent } from '../../interfaces/StockListingTableAEM.interface';
import StockHeader from '../../pages/StockListingPage/components/StockHeader';
import StockHeaderBar from '../../pages/StockListingPage/components/StockHeaderBar';
import StockTable from '../../pages/StockListingPage/components/StockTable';
import { getCustomerIdForAnalytics } from '../../utils/analytics-utils';
import { buildSortOptions } from '../../pages/StockListingPage/utils/stockListingHelpers';
import { normalizeBoolean } from '../../utils/normalizeBoolean';
import { fetchStockList } from '../../utils/stockListApi';
import {
    campaignName,
    journeyTag,
    productCategory,
    referenceId,
    segmentName,
    sprint,
} from '../../pages/News/newsTrackingConstants';
import { trackFailureScenarioRetryClick } from '../News/components/trackFailureScenarioRetry';
import NoResultsFound from '../NoResultsFound/NoResultsFound';

/**
 * AEM Wrapper Component for Stock Listing Table
 * 
 * This component:
 * - Receives AEM-authored properties from the dialog
 * - Fetches stock list data client-side
 * - Uses modular components (StockHeader, StockHeaderBar, StockTable)
 * - Handles loading, error, and empty states
 * 
 * Usage in AEM:
 * - Component resource type: 'your-app/components/stocklistingtable'
 * - Authors configure: title, entityType, sortValue, etc. via dialog
 */
const StockListingTableAEM: React.FC<StockListingTableAEMContent> = (props) => {
  const {
    title,
    headerText,
    entityType = "topgainers",
    showDisclaimer = true,
    sortValue = "amount_high_to_low",
    sortOptions: aemSortOptions,
    watchlist,
    errorState,
    emptyState,
    tableLabels,
    uiLabels = {},
    isEmpty = false,
  } = props;

  // Build sort options from AEM with fallbacks
  const sortOptions = buildSortOptions(aemSortOptions);

  const searchParams = useSearchParams();
  const [stockList, setStockList] = useState<IStockItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const shouldShowDisclaimer = normalizeBoolean(showDisclaimer, true);
  const isComponentEmpty = normalizeBoolean(isEmpty, false);

  // Resolve current sort from URL (if present) or from AEM-authored default
  const currentSort = searchParams.get("sort") ?? sortValue ?? "amount_high_to_low";

  // Fetch stock list data
  const fetchStockData = async () => {
    setIsLoading(true);
    setHasError(false);
    
    try {
      const data = await fetchStockList({
        entity: entityType || "topgainers",
        exchId: 0,
        page: 1,
        limit: 20,
        // Pass sort from dropdown / URL, e.g. "amount_low_to_high"
        sort: currentSort,
      });

      const stocks: IStockItem[] = data?.stockList ?? data?.data?.stockList ?? [];

      setStockList(stocks);
      setHasError(false);
    } catch (error) {
      console.error('Error fetching stock list:', error);
      setHasError(true);
      setStockList([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Don't fetch if component is empty (AEM authoring mode)
    if (isComponentEmpty) {
      setIsLoading(false);
      return;
    }

    fetchStockData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, currentSort, isComponentEmpty]);

  // Handle retry
  const handleRetry = () => {
    fetchStockData();
  };

  // EditConfig: Show placeholder when component is empty
  if (isComponentEmpty) {
    return (
      <div className="px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg">
        <div className="text-center py-8">
          <p className="text-gray-500 text-sm font-medium mb-2">
            Stock Listing Table Component
          </p>
          <p className="text-gray-400 text-xs">
            Configure this component in the AEM dialog
          </p>
        </div>
      </div>
    );
  }

  // Show error state
  if (hasError && !isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center py-12">
        <BFLApiFailure
          imageSrc={errorState?.imageSrc ?? ""}
          imageAlt={errorState?.imageAlt ?? "Error"}
          error={
            errorState?.title ??
            "Unable to fetch stock information"
          }
          errorDescription={
            errorState?.description ??
            "Something went wrong, please wait a moment, then try again"
          }
          retryButtonText={errorState?.retryButtonText ?? "Retry"}
          onRetry={handleRetry}
          trackingConfig={{
            sectionTitle: `${errorState?.title ?? "Stock Listing"} unable to load`,
            componentPosition: "1",
            entryPoint: "no_result_found",
            productCategory,
            subSection: "Stock Listing",
            segmentName,
            journeyTag,
            campaignName,
            sprint,
            referenceId,
          }}
          onRetryClick={(params) =>
            trackFailureScenarioRetryClick(params, getCustomerIdForAnalytics)
          }
        />
      </div>
    );
  }

  // Show empty state
  if (!isLoading && stockList.length === 0 && !hasError) {
    return (
      <div className="px-4 py-4">
        <NoResultsFound
          title={emptyState?.title}
          description={emptyState?.description}
          aemImages={
            emptyState?.imageSrc
              ? { noResultsImage: emptyState.imageSrc }
              : undefined
          }
        />
      </div>
    );
  }

  return (
    <>
      {/* Desktop header bar with sort */}
      {headerText && (
        <StockHeaderBar
          headerText={headerText}
          sortValue={currentSort}
          sortOptions={sortOptions}
          variant="desktop"
          uiLabels={uiLabels}
        />
      )}

      {/* Main content section */}
      <section
        className="mb-10 box-border block w-full max-w-[892px] border-[rgba(0,0,0,0.08)] bg-white leading-[19.2px] [unicode-bidi:isolate]  md:p-3"
        aria-label={title ?? (uiLabels.stockListingAriaLabel ?? "Stock listing")}
      >
        {/* Page header with title and disclaimer */}
        {title && <StockHeader title={title} showDisclaimer={shouldShowDisclaimer} disclaimerText={uiLabels.disclaimerText} />}

        {/* Mobile header bar with sort */}
        {headerText && (
          <StockHeaderBar
            headerText={headerText}
            sortValue={currentSort}
            sortOptions={sortOptions}
            variant="mobile"
            uiLabels={uiLabels}
          />
        )}

        {/* Stock table */}
        <StockTable
          data={stockList}
          entityType={entityType}
          isLoading={isLoading}
          tableLabels={tableLabels}
          uiLabels={uiLabels}
          watchlistProps={{
            modalTitle: watchlist?.modalTitle,
            modalBtnCta: watchlist?.modalBtnCta,
            successMsg: watchlist?.successMsg,
            errorMsg: watchlist?.errorMsg,
            cta_text: watchlist?.cta_text,
            section_title: watchlist?.section_title,
          }}
        />
      </section>
    </>
  );
};

// useSearchParams() requires a Suspense boundary on statically prerendered routes.
const SuspendedStockListingTableAEM = (
  props: React.ComponentProps<typeof StockListingTableAEM>,
) => (
  <Suspense fallback={null}>
    <StockListingTableAEM {...props} />
  </Suspense>
);

export default SuspendedStockListingTableAEM;

